import { SubgradeAreaMapping } from "../components/SubGrades/SubgradeAreaMapping";
import { SubgradeTableFields } from "../components/SubGrades/SubgradeTableFields";
import { FleetDataItem } from "../types/FleetDataItem";
import { SubgradeBackingData } from "../types/SubgradeBackingData";
import { VesselSubgradeDataItem } from "../types/VesselSubgradeDataItem";
import {VesselSubgradeResponse} from "../types/VesselSubgradeResponse";
export const getSubgradeBackingDataSectionAverages = (data: SubgradeBackingData) => {
    const averageScores: Record<string, number> = {};

    for (const [key, value] of Object.entries(data)) {
        averageScores[key] = value as number;
    }
    return averageScores;
}

export const calculateDeviationPercentages = (sectionAverages: any, backgroundData: any) => {
    const sectionDeviationPercentages: { [section: string]: number } = {};
    const backgroundAverages = getSubgradeBackingDataSectionAverages(backgroundData);

    for (const section in sectionAverages) {
        const backgroundSectionAverage = backgroundAverages[section];
        if (backgroundSectionAverage === null || backgroundSectionAverage === undefined || backgroundSectionAverage === 0) {
            continue;
        }

        let percentage = ((sectionAverages[section] - backgroundSectionAverage) / backgroundSectionAverage) * 100;

        if(!percentage){
            percentage = 0;
        }

        sectionDeviationPercentages[section] = percentage;
    }

    return sectionDeviationPercentages;
}

export const calculateTechnicalManagerDeviationPercentages = (technicalManagerAverages: any, backgroundDataAverage: number) => {
    const technicalManagerDeviationPercentages: { [technicalManager: string]: number } = {};

    for (const technicalManager in technicalManagerAverages) {
        const percentage = ((technicalManagerAverages[technicalManager] - backgroundDataAverage) / backgroundDataAverage) * 100;
        technicalManagerDeviationPercentages[technicalManager] = percentage;
    }

    return technicalManagerDeviationPercentages;
};

export const calculateAverageSectionGrade = (data: VesselSubgradeResponse) => {
    const sectionAverages: { [section: string]: number } = {};
    const sectionCounts: { [section: string]: number } = {};

    for (const vesselData of Object.values(data)) {
        for (const gradeTuple of Object.entries(vesselData.subGrades)) {
            const grade = gradeTuple[1];
            if (grade.score) {
                sectionAverages[grade.name] = (sectionAverages[grade.name] || 0) + grade.score;
                sectionCounts[grade.name] = (sectionCounts[grade.name] || 0) + 1;
            }
        }
    }

    for (const section in sectionAverages) {
        sectionAverages[section] /= sectionCounts[section] || 1;
    }

    return sectionAverages;
}

export const calculateAverageSectionGradeForTechnicalManagers = (data: VesselSubgradeResponse, gradeName: string) => {
    const technicalManagerAverages: { [technicalManager: string]: number } = {};
    const technicalManagerCounts: { [technicalManager: string]: number } = {};

    for (const vesselData of Object.values(data)) {
        for (const gradeTuple of Object.entries(vesselData.subGrades)) {
            const grade = gradeTuple[1];
            if (grade.name === gradeName && grade.score) {
                technicalManagerCounts[vesselData.technicalManager] = (technicalManagerCounts[vesselData.technicalManager] || 0) + 1;
                technicalManagerAverages[vesselData.technicalManager] = (technicalManagerAverages[vesselData.technicalManager] || 0) + grade.score;
            }
        }
    }

    for (const technicalManager in technicalManagerAverages) {
        technicalManagerAverages[technicalManager] /= technicalManagerCounts[technicalManager] || 1;
    }

    return technicalManagerAverages;
}

export const mapToGraphAxis = (deviationData: any, sectionAverages: any, trendData : any) => {
    const xValues: string[] = [];
    const yValues: number[] = [];

    Object.entries(deviationData).forEach(([section, deviation]) => {
        let xValue = section;

        const sectionAverage = sectionAverages[section] as number;
        if (sectionAverage >= 0) {
            xValue = xValue + ": " + sectionAverage.toFixed();
        }

        if(trendData){
            xValue += getTrendDecoration(trendData[section]);
        }

        xValues.push(xValue);

        const deviationPercentage = Math.round((deviation as number) * 100) / 100;
        yValues.push(deviationPercentage);
    });

    return {
        x: xValues,
        y: yValues,
    };
}

const toSectionName = (
    section: string,
    sectionAverages: Record<string, number>,
) => {
    const sectionAverage = sectionAverages[section] as number;
    const baseXValue = sectionAverage >= 0
        ? section
        : null;

    const xValue = baseXValue;

    return xValue;
};

export const mapToFilterOptions = (
    deviationData: Record<string, number>,
    sectionAverages: Record<string, number>,
): Array<string> => Object.entries(deviationData).map(([section]) =>
    toSectionName(section, sectionAverages)
).filter(Boolean) as Array<string>; // Have to use cast here, ther compiler doesnt acknowledge the boolean filter 

export const mapToGraphAxisForTechnicalManagers = (deviationData: any, sectionAverages: any) => {
    const xValues: string[] = [];
    const yValues: number[] = [];

    Object.entries(deviationData).forEach(([technicalManager, deviation]) => {
        let xValue = technicalManager;

        const sectionAverage = sectionAverages[technicalManager] as number;
        if (sectionAverage >= 0) {
            xValue = xValue + ": " + sectionAverage.toFixed();
        }

        xValues.push(xValue);

        const deviationPercentage = Math.round((deviation as number) * 100) / 100;
        yValues.push(deviationPercentage);
    });

    return {
        x: xValues,
        y: yValues,
    };
}

export const getTrendDecoration = (trend : string) => {
    const trendNumber = parseInt(trend);

    if(trendNumber > 0) {
        return '<span style="color: #53A440; font-size: 1rem"> <span style="color: black; font-size: 1rem"> |</span> ⬆</span>+' + trendNumber;
    }

    if(trendNumber == 0) {
        return '<span style="color: #68658C; font-size: 1rem"> <span style="color: black; font-size: 1rem"> |</span> ⮕</span>    ' + trendNumber;
    }

    if(trendNumber < 0) {
        return '<span style="color: #EC1E17; font-size: 1rem"> <span style="color: black; font-size: 1rem"> |</span> ⬇ </span>' + trendNumber;
    }
    return "";
}

export const mapVesselSubgradeData = (subgradeResponse: VesselSubgradeResponse, inspectionAndGradeResponse: FleetDataItem[]) => {
    let data : VesselSubgradeDataItem[] = [];
    Object.entries(subgradeResponse).forEach(x => {
        const imo = x[0];
        const subgradeDataForVessel = x[1];

        const inspectionData = inspectionAndGradeResponse.filter(x => x.vessel.imo == imo)[0];
        const latestGradeInspectionId = inspectionData?.inspection?.latestGrades?.inspectionId?.toLowerCase();
        const latestMatchingHistoricSubgrades = subgradeDataForVessel.subGradeHistory?.find(x => x.id.toLowerCase() === latestGradeInspectionId);

        if (!latestGradeInspectionId || !latestMatchingHistoricSubgrades) {
            return;
        }

        const getSubgrade = (key: string): number | string => {
            const fullName = (SubgradeAreaMapping)[key];
            const foundSubgrade = latestMatchingHistoricSubgrades.grades.find((subgrade) => subgrade.name.toUpperCase() === fullName.toUpperCase());

            if(!foundSubgrade){
                console.warn("No matching subgrade found for ", key)
            }

            if(!foundSubgrade?.points){
                return "N/A"
            }
            return foundSubgrade?.points;
        }

        const dataItem : VesselSubgradeDataItem = {
            idwalGrade: inspectionData.inspection.latestGrades?.idwalGrade,
            imo: subgradeDataForVessel.shipIMO,
            countryOfBuild: subgradeDataForVessel.countryOfBuild,
            shipAge: subgradeDataForVessel.shipAge,
            vesselName: inspectionData.vessel.vesselName,
            id: subgradeDataForVessel.id,
            subgrades: {
                bridgeAndNavigationEquipment: getSubgrade(SubgradeTableFields.SUBGRADES.BRIDGE_AND_NAVIGATION_EQUIPMENT),
                accomodation: getSubgrade(SubgradeTableFields.SUBGRADES.ACCOMMODATION),
                lifesavingAppliances: getSubgrade(SubgradeTableFields.SUBGRADES.LIFESAVING_APPLIANCES),
                mooringDecks: getSubgrade(SubgradeTableFields.SUBGRADES.MOORING_DECKS),
                engineRoomAndMachinery: getSubgrade(SubgradeTableFields.SUBGRADES.ENGINE_ROOM_AND_MACHINERY),
                vesselCapabilitiesAndCargoSystems: getSubgrade(SubgradeTableFields.SUBGRADES.VESSEL_CAPABILITIES_AND_CARGO_SYSTEMS),
                ballastTankAndSystems: getSubgrade(SubgradeTableFields.SUBGRADES.BALLAST_TANK_AND_SYSTEMS),
                weatherDecksAndFittings: getSubgrade(SubgradeTableFields.SUBGRADES.WEATHER_DECKS_AND_FITTINGS),
                hull: getSubgrade(SubgradeTableFields.SUBGRADES.HULL),
                pollutionControl: getSubgrade(SubgradeTableFields.SUBGRADES.POLLUTION_CONTROL),
                onboardManagement: getSubgrade(SubgradeTableFields.SUBGRADES.ONBOARD_MANAGEMENT),
                safeWorkingEnvironment: getSubgrade(SubgradeTableFields.SUBGRADES.SAFE_WORKING_ENVIRONMENT),
                forthcomingRegulatoryCompliance: getSubgrade(SubgradeTableFields.SUBGRADES.FORTHCOMING_REGULATORY_COMPLIANCE),
                crewWelfare: getSubgrade(SubgradeTableFields.SUBGRADES.CREW_WELFARE),
                crewPerformance: getSubgrade(SubgradeTableFields.SUBGRADES.CREW_PERFORMANCE),
                safetyManagement: getSubgrade(SubgradeTableFields.SUBGRADES.SAFETY_MANAGEMENT),
                plannedMaintenanceSystem: getSubgrade(SubgradeTableFields.SUBGRADES.PLANNED_MAINTENANCE_SYSTEM),
                classificationAndCertification: getSubgrade(SubgradeTableFields.SUBGRADES.CLASSIFICATION_AND_CERTIFICATION),
                pscPerformance: getSubgrade(SubgradeTableFields.SUBGRADES.PSC_PERFORMANCE),
                fireFightingEquipmentAndSystems: getSubgrade(SubgradeTableFields.SUBGRADES.FIRE_FIGHTING_EQUIPMENT_AND_SYSTEMS),
                designAndConstruction: getSubgrade(SubgradeTableFields.SUBGRADES.DESIGN_AND_CONSTRUCTION),
            },
            inspectionDate: subgradeDataForVessel.inspectionDate,
            location: inspectionData.inspection.location?.name,
            country:inspectionData.inspection.location?.country?.name,
            type: inspectionData.vessel.type,
            inspectionStatus: inspectionData.inspection.status,
            technicalManager: inspectionData.vessel.technicalManager,
            inspectionGuid: inspectionData.inspection?.latestGrades?.inspectionId!,
            aiGrades: inspectionData.aiGrades ?? undefined,
            dwt: inspectionData.vessel.dwt,
            teu: inspectionData.vessel.teu,
            conditionScore: inspectionData.inspection?.latestGrades?.conditionGrade!,
            managementScore: inspectionData.inspection?.latestGrades?.managementGrade!,
            subGradeHistory: subgradeDataForVessel.subGradeHistory,
        }

        data.push(dataItem);
    });

    return addCustomFieldFleetFiltersToData(data);
}

export const addCustomFieldFleetFiltersToData = (data: VesselSubgradeDataItem[]) => 
    data.map(item => {
        const x = {...item};
        x.nameFilterField = x.vesselName + " " + x.imo;
        x.locationFilterField = new Date(x.inspectionDate).toLocaleDateString("en-GB") + " " + x.location + x.country

        return x;
    });